import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchUpcomingEvents, fetchContactInfo, isEnglish, clock, useWebsiteImages, heroPhotos, eventImageFor, eventSlot } from '../lib/publicContent';
import { ed, useEditing, usePageMeta } from '../lib/editTexts';
import PublicNav from '../components/public/PublicNav';
import MapConsent from '../components/public/MapConsent';
import '../styles/public.css';

// Fotos und Terminbilder kommen aus lib/publicContent (AgoraHub-Plaetze, sonst eingebaut).
const fmt = (d, opts, locale = 'de-DE') => { try { return new Intl.DateTimeFormat(locale, opts).format(new Date(d)); } catch { return ''; } };
const fmtDay = (d, l) => fmt(d, { weekday: 'long' }, l);
const fmtDate = (d, l) => fmt(d, { day: 'numeric', month: 'long' }, l);
const fmtTime = (d, l) => fmt(d, { hour: '2-digit', minute: '2-digit' }, l);

export default function Home() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const locale = isEnglish(lang) ? 'en-GB' : 'de-DE';
  const [slide, setSlide] = useState(0);
  const [events, setEvents] = useState([]);
  const [contactInfo, setContactInfo] = useState(null);
  const timer = useRef(null);
  const editing = useEditing();
  const slots = useWebsiteImages();
  const PHOTOS = heroPhotos(slots);
  usePageMeta('home');

  const startTimer = () => {
    clearInterval(timer.current);
    if (editing) return;   // Bearbeitungsmodus: Slideshow steht
    timer.current = setInterval(() => setSlide(s => (s + 1) % PHOTOS.length), 5000);
  };
  useEffect(() => { startTimer(); return () => clearInterval(timer.current); }, [PHOTOS.length, editing]); // eslint-disable-line react-hooks/exhaustive-deps
  const goSlide = i => { setSlide(i); startTimer(); };

  useEffect(() => {
    fetchUpcomingEvents(10).then(setEvents).catch(() => {});
  }, []);

  useEffect(() => {
    fetchContactInfo().then(setContactInfo).catch(() => {});
  }, []);

  const loc = contactInfo?.locations?.find(l => l.active);
  const times = contactInfo?.service_times;
  const email = contactInfo?.contact_details?.email?.address || 'welcome@kacberlin.com';
  const mapsQ = loc ? encodeURIComponent(`${loc.addressLine1}, ${loc.addressLine2}`) : '';
  const mapsUrl = mapsQ ? `https://www.google.com/maps/search/?api=1&query=${mapsQ}` : '#';
  const embedUrl = mapsQ ? `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${mapsQ}` : null;

  return (
    <div className="kac-public">
      <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 26px 60px' }}>
        <PublicNav />

        {/* HERO SPLIT */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.02fr 1fr', borderRadius: 30, overflow: 'hidden', boxShadow: '0 24px 80px rgba(30,18,10,0.18)', minHeight: 720 }}>

          {/* Left – red */}
          <div style={{ background: 'radial-gradient(120% 120% at 12% 8%, #CA3127 0%, #B12820 48%, #97211A 100%)', color: '#fff', padding: '52px 60px 56px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 64 }}>
              {/* Helles Wappen: Platz brand.logo_inverse gibt es im AgoraHub-Katalog noch nicht -> eingebautes Bild */}
              <img src="/kac-assets/LOGOw.png" alt={t('home.logo_title')} style={{ height: 112, filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.22))' }} />
              <div>
                <div {...ed('home.logo_title')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 24 }}>{t('home.logo_title')}</div>
                <div {...ed('home.logo_subtitle')} style={{ fontSize: 13, opacity: 0.72, marginTop: 2 }}>{t('home.logo_subtitle')}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.6)' }} />
              <span {...ed('home.welcome_to')} style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.8 }}>{t('home.welcome_to')}</span>
            </div>
            <h1 {...ed('home.title')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 58, lineHeight: 0.98, margin: '0 0 26px', maxWidth: 500 }}>
              {t('home.title')}
            </h1>
            <p {...ed('home.tagline')} style={{ fontSize: 18, lineHeight: 1.55, opacity: 0.88, maxWidth: 420, marginBottom: 48 }}>
              {t('home.tagline')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 32px', marginTop: 'auto' }}>
              {[
                { key: 'home.day.sunday', time: clock(lang, times?.sunday?.time || '16:30') },
                { key: 'home.day.thursday', time: clock(lang, times?.thursday?.time || '19:00') },
              ].map(({ key, time }) => (
                <div key={key}>
                  <div {...ed(key)} style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.6, marginBottom: 4 }}>{t(key)}</div>
                  <div data-edit-central="contact_info" style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 26 }}>{time}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right – slideshow */}
          <div style={{ background: '#1c1714', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
              {PHOTOS.map((src, i) => (
                <div key={src} data-slot="home.hero" data-slot-index={i} style={{ position: 'absolute', inset: 0, backgroundImage: `url("${src}")`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: i === slide ? 1 : 0, pointerEvents: i === slide ? 'auto' : 'none', transform: i === slide ? 'scale(1.05)' : 'scale(1)', transition: 'opacity 1.4s ease, transform 7s ease' }} />
              ))}
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(to top, rgba(20,12,8,0.45) 0%, transparent 50%)' }} />
              <div style={{ position: 'absolute', bottom: 24, left: 24, right: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                  <span {...ed('home.slide_caption')}>{t('home.slide_caption')}</span> · <span data-edit-central="contact_info">{loc?.addressLine1 || t('nav.brand')}</span>
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  {PHOTOS.map((_, i) => (
                    <button key={i} type="button" aria-label={`${t('home.slide_label')} ${i + 1}`} onClick={() => goSlide(i)} style={{ width: i === slide ? 22 : 8, height: 8, borderRadius: 999, border: 'none', padding: 0, cursor: 'pointer', background: i === slide ? '#fff' : 'rgba(255,255,255,0.45)', transition: 'background .3s ease, width .3s ease' }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EVENTS – Termine pflegt die Gemeinde im AgoraHub-Kalender */}
        <div style={{ marginTop: 72 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 32 }}>
            <div>
              <div className="kp-kicker" style={{ marginBottom: 8 }} {...ed('home.events_kicker')}>{t('home.events_kicker')}</div>
              <h2 {...ed('home.upcoming_events')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 40, margin: 0 }}>{t('home.upcoming_events')}</h2>
            </div>
          </div>
          <div className="kac-scroll" data-edit-central="events" style={{ display: 'flex', gap: 20, overflowX: 'auto', scrollSnapType: 'x mandatory', paddingBottom: 12 }}>
            {(events.length ? events : Array(6).fill(null)).map((ev, idx) => {
              const isRed = idx % 5 === 4;
              const d = ev?.start || ev?.start_time || ev?.date || '';
              return (
                <div key={ev?.id || ev?.event_id || idx} style={{ flex: '0 0 260px', scrollSnapAlign: 'start', borderRadius: 10, overflow: 'hidden', background: isRed ? '#C0291F' : '#FBF8F2', border: isRed ? 'none' : '1px solid rgba(120,100,70,0.22)', color: isRed ? '#fff' : '#26211C', display: 'flex', flexDirection: 'column' }}>
                  <div data-slot={eventSlot(ev)} style={{ height: 155, backgroundImage: `linear-gradient(transparent 35%, rgba(20,12,8,0.3)), url("${eventImageFor(ev, slots)}")`, backgroundSize: 'cover', backgroundPosition: 'center', flexShrink: 0 }} />
                  <div style={{ padding: '14px 16px 16px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: isRed ? 'rgba(255,255,255,0.65)' : '#857A6E', marginBottom: 5 }}>
                        {d ? `${fmtDay(d, locale)} · ${fmtDate(d, locale)}` : ' '}
                      </div>
                      <div style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 17, lineHeight: 1.2, marginBottom: 5 }}>
                        {ev?.title || ev?.summary || ' '}
                      </div>
                      {ev?.location && <div style={{ fontSize: 12, opacity: 0.65 }}>{ev.location}</div>}
                    </div>
                    {d && <div style={{ fontSize: 13, fontWeight: 700, marginTop: 8, color: isRed ? 'rgba(255,255,255,0.85)' : '#C0291F' }}>{clock(lang, fmtTime(d, locale))}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* BESUCHE UNS */}
        <div style={{ marginTop: 64 }}>
          <div className="kp-kicker" style={{ marginBottom: 8 }} {...ed('home.visit_kicker')}>{t('home.visit_kicker')}</div>
          <h2 {...ed('home.visit_us')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 40, margin: '0 0 40px' }}>{t('home.visit_us')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48 }}>
            <div>
              <p {...ed('home.visit_text')} style={{ fontSize: 17, lineHeight: 1.65, color: '#5e554b', maxWidth: 400, marginTop: 0, marginBottom: 28 }}>
                {t('home.visit_text')}
              </p>
              <div data-edit-central="contact_info" style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 26, lineHeight: 1.2, marginBottom: 16 }}>
                {loc?.addressLine1 || 'Ringbahnstraße 32-34'}<br />{loc?.addressLine2 || '12099 Berlin-Tempelhof'}
              </div>
              <a href={`mailto:${email}`} data-edit-central="contact_info" style={{ fontSize: 15, fontWeight: 600, color: '#C0291F', display: 'block', marginBottom: 28, textDecoration: 'none' }}>{email}</a>
              <div style={{ display: 'flex', gap: 12 }}>
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="kac-btn-red"><span {...ed('home.plan_route')}>{t('home.plan_route')}</span> →</a>
                <a href={`mailto:${email}`} className="kac-btn-ghost" {...ed('home.contact')}>{t('home.contact')}</a>
              </div>
            </div>
            <div style={{ borderRadius: 8, overflow: 'hidden', minHeight: 280, background: '#E8E0D4' }}>
              <MapConsent embedUrl={embedUrl} mapsUrl={mapsUrl} height={280} />
            </div>
          </div>
        </div>

        {/* FOOTER – gleiche Kennungen wie PublicFooter und die Handy-Fusszeile */}
        <footer style={{ borderTop: '1px solid rgba(120,100,70,0.22)', marginTop: 64, paddingTop: 28, display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#9b8f7f' }}>
          <span><span {...ed('footer.copyright')}>{t('footer.copyright')}</span> · <span {...ed('footer.founded')}>{t('footer.founded')}</span></span>
          <div style={{ display: 'flex', gap: 20 }}>
            <Link to="/support" style={{ color: '#9b8f7f', textDecoration: 'none' }} {...ed('footer.support')}>{t('footer.support')}</Link>
            <Link to="/privacy" style={{ color: '#9b8f7f', textDecoration: 'none' }} {...ed('footer.privacy')}>{t('footer.privacy')}</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
